import React, { useEffect, useState, useRef } from 'react';
import {
  XMarkIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Tooltip,
} from 'recharts';
import { ADVANCED_INTERVIEW_TYPE_LABELS } from '../../../constants/advancedInterviewTypes';
import InterviewProctoringContent from './InterviewProctoringContent';
import SessionVideoPlayer from '../../../components/results/SessionVideoPlayer';
import LanguageDisplayToggle from '../../../components/language/LanguageDisplayToggle';

/* ───── Score Ring (SVG radial) ───── */
function ScoreRing({ value, size = 64, strokeWidth = 5 }) {
  const r = (size - strokeWidth * 2) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(100, Math.max(0, value ?? 0));
  const offset = circ - (pct / 100) * circ;
  const color = pct >= 80 ? '#059669' : pct >= 60 ? '#d97706' : '#dc2626';
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={strokeWidth} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={color} strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        style={{ transition: 'stroke-dashoffset 0.8s ease' }}
      />
    </svg>
  );
}

/* ───── Normalize text (for dedup + translation lookup) ───── */
const normalizeText = (s) =>
  (s || '')
    .replace(/[.,/#!$%^&*;:{}=\-_`~()?]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/* ───── Deduplicate Transcript Turns ───── */
const deduplicateTranscriptTurns = (turns) => {
  if (!turns || !Array.isArray(turns)) return [];
  const deduped = [];
  for (const turn of turns) {
    const text = typeof turn.text === 'string' ? turn.text : (turn.text?.text || turn.content || turn.message || '');
    const turnNorm = normalizeText(text);
    if (!turnNorm) continue;
    let isDup = false;
    const start = Math.max(0, deduped.length - 5);
    for (let i = deduped.length - 1; i >= start; i--) {
      const prev = deduped[i];
      if (prev.role !== turn.role) continue;
      const prevText = typeof prev.text === 'string' ? prev.text : (prev.text?.text || prev.content || prev.message || '');
      const prevNorm = normalizeText(prevText);
      if (prevNorm === turnNorm) { isDup = true; break; }
      if (prevNorm.length >= 15 && turnNorm.length >= 15) {
        if (prevNorm.includes(turnNorm)) { isDup = true; break; }
        if (turnNorm.includes(prevNorm)) {
          // Keep the longer version
          deduped[i] = { ...deduped[i], text: turn.text };
          isDup = true;
          break;
        }
      }
    }
    if (!isDup) deduped.push(turn);
  }
  return deduped;
};

/* ───── Interview Session Modal ───── */
const InterviewSessionModal = ({
  session,
  interviewDetails,
  onClose,
  initialTab = 'combined',
  // When set, the modal is rendered on the public hiring-manager review page:
  // data is fetched through the share-code-scoped public endpoints and the
  // recruiter-only scoring dev-tool is disabled. Absent => recruiter behavior.
  publicShareCode = '',
}) => {
  // On a public share link 'proctoring' is not a reachable tab (see Tabs Navigation
  // below), but CandidatesTable opens this modal with initialTab='proctoring' from
  // its proctoring risk chips. Left unguarded that lands the hiring manager on a
  // tab with no way back — the panel renders and fetches while its nav button is
  // hidden. Collapse it to the combined tab instead.
  const requestedTab =
    publicShareCode && initialTab === 'proctoring' ? 'combined' : initialTab;
  const [activeTab, setActiveTab] = useState(requestedTab === 'analysis' || requestedTab === 'transcript' || requestedTab === 'overview' ? 'combined' : requestedTab);
  const [innerTab, setInnerTab] = useState('questions');
  const [interviewProctoringData, setInterviewProctoringData] = useState(null);
  const [interviewProctoringLoading, setInterviewProctoringLoading] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  // On-demand English rendering for non-English interviews.
  //   - Recruiter sees a "Translate to English" button in the modal header.
  //   - Clicking it POSTs to /translate-transcript, which returns the LLM
  //     translation after ~30–60s. Result persists on the session; subsequent
  //     opens skip straight to the Original / English toggle.
  //   - Failure: spinner disappears, button reappears silently.
  //   - English interviews: nothing rendered.
  //
  // displayLang: 'hi' means "show Original", 'en' means "show English"
  // (kept as literal codes because LanguageDisplayToggle uses those keys
  // internally — the visible labels are overridden via the `labels` prop).
  const [displayLang, setDisplayLang] = useState('hi');
  const [translating, setTranslating] = useState(false);
  // Locally-held translation data that overrides the session prop while the
  // parent hasn't refetched yet. Populated when the endpoint returns.
  const [localTranscriptEnglish, setLocalTranscriptEnglish] = useState(null);
  const [localTranscriptEnglishStatus, setLocalTranscriptEnglishStatus] = useState(null);

  // Source-of-truth for the interview language: prefer session.audioConfig
  // (persisted per-session, always accurate for what actually ran) and fall
  // back to interviewDetails.config (which the parent hook fetches separately
  // and may be missing on some paths).
  const interviewLanguageRaw =
    session?.audioConfig?.language || interviewDetails?.config?.language;
  const interviewLanguage = (() => {
    if (typeof interviewLanguageRaw !== 'string') return 'en';
    const v = interviewLanguageRaw.trim().toLowerCase();
    if (v === 'hindi') return 'hi';
    if (v === 'hinglish') return 'hi-en';
    if (v === 'english') return 'en';
    return v || 'en';
  })();
  const isNonEnglishInterview = interviewLanguage !== 'en';

  const effectiveTranscriptEnglish = localTranscriptEnglish
    || (Array.isArray(session?.transcriptEnglish) ? session.transcriptEnglish : null);
  const effectiveStatus = localTranscriptEnglishStatus || session?.transcriptEnglishStatus || null;
  const hasStoredTranslation =
    effectiveStatus === 'ready' &&
    effectiveTranscriptEnglish &&
    effectiveTranscriptEnglish.length > 0;

  const showLanguageToggle = isNonEnglishInterview && hasStoredTranslation;
  // Never offer "Translate to English" on a public share link: handleTranslateClick
  // POSTs to /api/ai-interviews/session/:id/translate-transcript, which is
  // auth-only and has no share-code equivalent — so the request 401s and fails
  // silently, leaving a hiring manager clicking a button that can never work.
  // The language *toggle* above stays: it is client-side and only appears when a
  // translation was already stored by a recruiter.
  const showTranslateButton =
    !publicShareCode && isNonEnglishInterview && !hasStoredTranslation && !translating;
  const showTranslatingSpinner = isNonEnglishInterview && translating;
  const showEnglish = showLanguageToggle && displayLang === 'en';

  // Normalized-text lookup: original text (from the translator's stored
  // metadata) → English rendering. Robust to any downstream reshaping of
  // session.transcript because it doesn't depend on array indices or
  // timestamps — only on the source text itself.
  const englishByOriginal = React.useMemo(() => {
    const m = new Map();
    if (!effectiveTranscriptEnglish) return m;
    for (const turn of effectiveTranscriptEnglish) {
      const orig = turn?.metadata?.originalText;
      const eng = turn?.text;
      if (typeof orig === 'string' && typeof eng === 'string') {
        const key = normalizeText(orig);
        if (key) m.set(key, eng);
      }
    }
    return m;
  }, [effectiveTranscriptEnglish]);
  const rawTurnText = (turn) => (typeof turn?.text === 'string'
    ? turn.text
    : (turn?.text?.text || turn?.content || turn?.message || ''));
  const pickTurnText = (turn) => {
    if (!showEnglish || !hasStoredTranslation) return rawTurnText(turn);
    const key = normalizeText(rawTurnText(turn));
    return englishByOriginal.get(key) || rawTurnText(turn);
  };

  const handleTranslateClick = async () => {
    const sessionId = session?.id || session?._id;
    if (!sessionId || translating) return;
    setTranslating(true);
    try {
      const res = await axios.post(`/api/ai-interviews/session/${sessionId}/translate-transcript`);
      const data = res.data || {};
      if (data.transcriptEnglishStatus === 'ready' && Array.isArray(data.transcriptEnglish) && data.transcriptEnglish.length > 0) {
        setLocalTranscriptEnglish(data.transcriptEnglish);
        setLocalTranscriptEnglishStatus('ready');
        setDisplayLang('en'); // Auto-switch to English on success.
      }
      // Any other outcome (missing/failed): spinner disappears, button
      // reappears — silent per product spec.
    } catch (err) {
      // Silent failure — button reappears.
    } finally {
      setTranslating(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'proctoring' || interviewProctoringData || interviewProctoringLoading) return;
    const sessionId = session?.id || session?._id;
    if (!sessionId) return;
    setInterviewProctoringLoading(true);
    const proctoringUrl = publicShareCode
      ? `/api/screenings/public/candidate-review/${publicShareCode}/interview-session/${sessionId}/proctoring`
      : `/api/ai-interviews/session/${sessionId}/proctoring`;
    axios.get(proctoringUrl)
      .then((res) => {
        const d = res.data;
        // Merge top-level riskScore/riskLevel/categories into the session object
        // so CandidateProctoringDetailsContent can read session.riskScore etc.
        setInterviewProctoringData({
          ...(d.session || {}),
          riskScore: d.riskScore,
          riskLevel: d.riskLevel,
          violations: d.categories,
          categories: d.categories,
          flagReasonsInEnglish: d.flagReasonsInEnglish,
          aiSummary: d.aiSummary,
        });
      })
      .catch(() => setInterviewProctoringData({ error: true, message: 'Failed to load proctoring details.' }))
      .finally(() => setInterviewProctoringLoading(false));
  }, [activeTab, session, interviewProctoringData, interviewProctoringLoading, publicShareCode]);
  const [showScoringCompare, setShowScoringCompare] = useState(false);
  const [compareTab, setCompareTab] = useState('new');
  const [dualScores, setDualScores] = useState(null);
  const [rescoreLoading, setRescoreLoading] = useState(false);
  const [titleTapCount, setTitleTapCount] = useState(0);

  const toggleScoringCompareMode = () => {
    // Recruiter-only scoring dev-tool: never available on the public review page.
    if (publicShareCode) return;
    setShowScoringCompare((prev) => {
      const next = !prev;
      if (!next) {
        setDualScores(null);
        setCompareTab('new');
      }
      toast(next ? 'Scoring compare mode enabled' : 'Scoring compare mode disabled');
      return next;
    });
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      const isToggleShortcut =
        event.ctrlKey && event.shiftKey && (event.code === 'KeyY' || event.key?.toLowerCase() === 'y');

      if (isToggleShortcut) {
        event.preventDefault();
        event.stopPropagation();
        toggleScoringCompareMode();
      }
    };

    // Capture phase helps us win against other key handlers in the page.
    window.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, []);

  useEffect(() => {
    setDualScores(null);
    setCompareTab('new');
    setShowScoringCompare(false);
    setTitleTapCount(0);
    // Reset per-modal-open translation state — a different session may
    // already have a cached translation, or none at all.
    setLocalTranscriptEnglish(null);
    setLocalTranscriptEnglishStatus(null);
    setTranslating(false);
    setDisplayLang('hi');
  }, [session?.id]);

  // Removed old video cleanup since it's handled in the component


  useEffect(() => {
    if (titleTapCount < 5) return;
    setTitleTapCount(0);
    toggleScoringCompareMode();
  }, [titleTapCount]);

  const triggerDualRescore = async () => {
    if (!session?.id) return;
    setRescoreLoading(true);
    try {
      const res = await axios.post(`/api/ai-interviews/session/${session.id}/rescore-dual`);
      setDualScores({ old: res.data?.old || null, new: res.data?.new || null });
      toast.success('Old and new scoring updated');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to rescore');
    } finally {
      setRescoreLoading(false);
    }
  };

  const sessionForAnalysis = showScoringCompare
    ? compareTab === 'old'
      ? dualScores?.old || session
      : dualScores?.new || session
    : session;

  const advancedType = interviewDetails?.advancedType || null;

  if (!session) return null;

  const TAB = (id, label, Icon) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors whitespace-nowrap ${activeTab === id
          ? 'border-indigo-500 text-indigo-600'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
        }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[110] bg-black/40 backdrop-blur-[2px] overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="relative w-11/12 md:w-5/6 lg:w-4/5 xl:w-3/4 shadow-xl rounded-lg border bg-white max-h-[95vh] overflow-hidden flex flex-col">

        {/* ── Sticky Header ── */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3
                className="text-2xl font-bold text-gray-900 select-none leading-tight"
                onClick={() => setTitleTapCount((prev) => prev + 1)}
              >
                Interview Replay &amp; Analysis
              </h3>
              <p className="mt-0.5 text-sm text-gray-500">
                <span className="font-medium text-gray-700">{session.candidateName || 'Anonymous Candidate'}</span>
                {interviewDetails?.title && <> &nbsp;·&nbsp; {interviewDetails.title}</>}
              </p>
            </div>
            {/* Score pill in header */}
            <div className="flex items-center gap-3 ml-auto">
              {showLanguageToggle && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">View</span>
                  <LanguageDisplayToggle
                    value={displayLang}
                    onChange={setDisplayLang}
                    labels={{ hi: 'Original', en: 'English' }}
                  />
                </div>
              )}
              {showTranslateButton && (
                <button
                  type="button"
                  onClick={handleTranslateClick}
                  className="inline-flex items-center gap-1.5 h-7 px-3 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-semibold hover:bg-indigo-100 transition-colors"
                >
                  Translate to English
                </button>
              )}
              {showTranslatingSpinner && (
                <div className="inline-flex items-center gap-2 h-7 px-3 rounded-md border border-gray-200 bg-white text-gray-600 text-xs">
                  <svg className="animate-spin h-3.5 w-3.5 text-indigo-500" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                    <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Translating…
                </div>
              )}
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* ── Tabs Navigation ── */}
        <div className="border-b border-gray-200 bg-gray-50 px-6 flex items-center justify-between gap-4">
          <nav className="flex gap-6 -mb-px">
            {TAB('combined', 'Analysis & Transcript', ChartBarIcon)}
            {/* Proctoring is recruiter-only: a hiring manager reviewing a share
                link gets scores and transcript, not the candidate's integrity
                telemetry. Matches the public assessment modal, which likewise
                renders score content with no proctoring tab. */}
            {!publicShareCode && TAB('proctoring', 'Proctoring Details', ShieldCheckIcon)}
          </nav>
          {showScoringCompare && (
            <button
              type="button"
              onClick={triggerDualRescore}
              disabled={rescoreLoading}
              className="ml-auto shrink-0 inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-lg border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-60 transition-colors"
            >
              {rescoreLoading ? 'Rescoring...' : 'Rescore Old + New'}
            </button>
          )}
        </div>

        {/* ── Tab Content — Scrollable ── */}
        <div className="flex-1 overflow-y-auto px-6 py-6">

          
          {/* ════════ COMBINED ANALYSIS & TRANSCRIPT TAB ════════ */}
          {activeTab === 'combined' && (
            <div className="space-y-6">
              
              {/* TOP SECTION: Video Player + Inner Tabs */}
              <div className="flex flex-col xl:flex-row gap-5 xl:items-stretch" style={{ minHeight: '340px' }}>
                
                {/* LEFT: Video Player Box */}
                <div className="w-full xl:basis-[40%] xl:flex-none flex-shrink-0" style={{ minHeight: '260px' }}>
                  <SessionVideoPlayer
                    session={session}
                    interviewDetails={interviewDetails}
                    context="interview"
                    publicShareCode={publicShareCode}
                  />
                </div>

                {/* RIGHT: Inner Tabs (Q Analysis / Transcript) */}
                <div className="w-full xl:basis-[60%] flex flex-col bg-gradient-to-br from-white via-white to-slate-50 border border-gray-200 rounded-2xl shadow-lg overflow-hidden" style={{ minHeight: '260px', maxHeight: '600px' }}>
                  <div className="flex border-b border-gray-100 bg-gray-50/50">
                    <button
                      className={`flex-1 py-3.5 px-4 text-sm font-bold flex items-center justify-center gap-2 transition-colors ${innerTab === 'questions' ? 'text-indigo-600 bg-white border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                      onClick={() => setInnerTab('questions')}
                    >
                      <ClipboardDocumentCheckIcon className="w-4 h-4" />
                      Question Analysis
                    </button>
                    <button
                      className={`flex-1 py-3.5 px-4 text-sm font-bold flex items-center justify-center gap-2 transition-colors ${innerTab === 'transcript' ? 'text-indigo-600 bg-white border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                      onClick={() => setInnerTab('transcript')}
                    >
                      <ChatBubbleLeftRightIcon className="w-4 h-4" />
                      Transcript
                    </button>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto scrollbar-thin-light bg-gray-50/30 p-4">
                    {innerTab === 'questions' && (
                      <div className="h-full">
                        
              {sessionForAnalysis.rubric?.perQuestion &&
                sessionForAnalysis.rubric.perQuestion.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                      <ClipboardDocumentCheckIcon className="h-4 w-4 text-indigo-500" />
                      <h4 className="text-sm font-bold text-gray-900">Question Performance Analysis</h4>
                      <span className="ml-auto text-xs text-gray-400">
                        {sessionForAnalysis.rubric.perQuestion.length} questions
                      </span>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {sessionForAnalysis.rubric.perQuestion.map((q, idx) => {
                        const questionKey = `question-${q.index || idx}`;
                        const isExpanded = expandedQuestions.has(questionKey);
                        const score = q.score ?? 0;
                        const scoreBorder =
                          score >= 80
                            ? 'border-l-emerald-400'
                            : score >= 60
                              ? 'border-l-amber-400'
                              : score > 0
                                ? 'border-l-orange-400'
                                : 'border-l-red-400';
                        const scoreTextCls =
                          score >= 80
                            ? 'text-emerald-600'
                            : score >= 60
                              ? 'text-amber-600'
                              : 'text-red-600';
                        const scoreBgCls =
                          score >= 80
                            ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                            : score >= 60
                              ? 'bg-amber-50 border-amber-200 text-amber-700'
                              : 'bg-red-50 border-red-200 text-red-700';
                        const toggleQ = () => {
                          const next = new Set(expandedQuestions);
                          if (next.has(questionKey)) next.delete(questionKey);
                          else next.add(questionKey);
                          setExpandedQuestions(next);
                        };
                        return (
                          <div key={idx} className={`border-l-4 ${scoreBorder}`}>
                            <button
                              onClick={toggleQ}
                              className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex items-center gap-4 flex-1 min-w-0">
                                {/* Circular Score */}
                                <div className="flex-shrink-0 relative w-12 h-12 flex items-center justify-center">
                                  <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
                                    <circle cx="18" cy="18" r="15.5" fill="none" stroke="#f1f5f9" strokeWidth="3.2" />
                                    <circle cx="18" cy="18" r="15.5" fill="none" stroke={score >= 80 ? '#059669' : score >= 60 ? '#d97706' : score > 0 ? '#ea580c' : '#dc2626'} strokeWidth="3.2"
                                      strokeDasharray={`${Math.min(score, 100) * 0.975} 100`}
                                      strokeLinecap="round"
                                      className="transition-all duration-500"
                                    />
                                  </svg>
                                  <span className="absolute inset-0 flex items-center justify-center text-[13px] font-extrabold" style={{ color: score >= 80 ? '#059669' : score >= 60 ? '#d97706' : score > 0 ? '#ea580c' : '#dc2626' }}>{score}</span>
                                </div>
                                {/* Question info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="text-sm font-bold text-gray-800">
                                      Q{(q.index ?? idx) + 1}
                                    </span>
                                    <span
                                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${q.type === 'yes_no'
                                          ? 'bg-blue-100 text-blue-700'
                                          : 'bg-purple-100 text-purple-700'
                                        }`}
                                    >
                                      {q.type === 'yes_no' ? 'Yes / No' : 'Short Answer'}
                                    </span>
                                    {q.critical && (
                                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                                        Critical
                                      </span>
                                    )}
                                  </div>
                                  {/* Question prompt is authored in English on
                                      the job config — never translate it. */}
                                  <p className="text-sm text-gray-700 leading-relaxed line-clamp-2">
                                    {q.prompt}
                                  </p>
                                </div>
                              </div>
                              <div className="ml-4 flex-shrink-0">
                                {isExpanded ? (
                                  <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                                ) : (
                                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                                )}
                              </div>
                            </button>

                            {isExpanded && (
                              <div className="px-5 pb-5 pt-1 bg-gray-50/60 border-t border-gray-100 space-y-4">
                                {/* Expected Answer (yes/no) */}
                                {q.type === 'yes_no' && q.expectedAnswer && (
                                  <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
                                    <p className="text-xs font-semibold text-blue-700 mb-1">Expected Answer</p>
                                    <p className="text-sm text-blue-900 font-medium">{q.expectedAnswer}</p>
                                  </div>
                                )}
                                {/* Expected Points */}
                                {q.expectedPoints && q.expectedPoints.length > 0 && (
                                  <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Expected Points</p>
                                    <div className="flex flex-wrap gap-2">
                                      {q.expectedPoints.map((point, pIdx) => (
                                        <span
                                          key={pIdx}
                                          className="text-xs px-3 py-1.5 rounded-lg font-medium bg-white border border-gray-200 text-gray-700"
                                        >
                                          {point}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {/* Candidate Response */}
                                {q.responses && q.responses.length > 0 && (
                                  <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                      Candidate Response{q.responses.length > 1 ? 's' : ''}
                                    </p>
                                    <div className="space-y-2">
                                      {q.responses.map((resp, rIdx) => {
                                        // Look up the English rendering via the same
                                        // normalized-text map. Falls back to the raw
                                        // response if translation for this exact text
                                        // isn't found (e.g. rubric-rebuilt paraphrase).
                                        const displayText = showEnglish && hasStoredTranslation
                                          ? (englishByOriginal.get(normalizeText(resp)) || resp)
                                          : resp;
                                        return (
                                          <div
                                            key={rIdx}
                                            className="text-sm text-gray-800 bg-white rounded-lg border border-gray-200 px-4 py-3 leading-relaxed"
                                          >
                                            {displayText}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                {/* Relevant Transcript */}
                                {(() => {
                                  const segment = deduplicateTranscriptTurns(q.transcriptSegment);
                                  if (!segment || segment.length === 0) return null;
                                  return (
                                    <div>
                                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                                        <ChatBubbleLeftRightIcon className="h-3.5 w-3.5 text-indigo-400" />
                                        Relevant Transcript
                                      </p>
                                      <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2 max-h-56 overflow-y-auto scrollbar-thin-light">
                                        {segment.map((turn, tIdx) => (
                                          <div
                                            key={`seg-${idx}-${tIdx}`}
                                            className={`flex ${turn.role === 'ai' ? 'justify-start' : 'justify-end'}`}
                                          >
                                            <div
                                              className={`px-3 py-2 rounded-lg text-sm max-w-[85%] ${turn.role === 'ai'
                                                  ? 'bg-gray-100 border border-gray-200 text-gray-900'
                                                  : 'bg-blue-50 border border-blue-100 text-blue-900'
                                                }`}
                                            >
                                              <div className="text-[10px] font-bold uppercase tracking-wider mb-0.5 opacity-50">
                                                {turn.role === 'ai' ? 'Interviewer' : 'Candidate'}
                                              </div>
                                              <p className="leading-relaxed">
                                                {pickTurnText(turn)}
                                              </p>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })()}
                                {/* Evaluation Notes */}
                                {q.notes && (
                                  <div className="pt-3 border-t border-gray-200">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                                      <InformationCircleIcon className="h-3.5 w-3.5 text-amber-400" />
                                      Evaluation Notes
                                    </p>
                                    <p className="text-sm text-gray-700 italic leading-relaxed">
                                      {(String(q.notes).match(/[^.]+(?:\.|$)/g) || [String(q.notes)])
                                        .map((s) => s.trim())
                                        .filter((s) => !/could not be normalized/i.test(s))
                                        .join(' ')
                                        .trim()}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              
                      </div>
                    )}
                    {innerTab === 'transcript' && (
                      <div className="h-full bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
                        <div className="flex-1 overflow-y-auto scrollbar-thin-light p-4">
                          {session.transcript && session.transcript.length > 0 ? (
                            (() => {
                              const transcript = deduplicateTranscriptTurns(session.transcript);
                              if (transcript.length === 0) {
                                return <div className="text-center py-12 text-sm text-gray-400">No transcript available</div>;
                              }
                              const groups = [];
                              let current = { role: transcript[0].role, turns: [transcript[0]] };
                              for (let i = 1; i < transcript.length; i++) {
                                if (transcript[i].role === current.role) {
                                  current.turns.push(transcript[i]);
                                } else {
                                  groups.push(current);
                                  current = { role: transcript[i].role, turns: [transcript[i]] };
                                }
                              }
                              groups.push(current);
                              return groups.map((group, groupIdx) => (
                                <div key={`tg-${groupIdx}-${group.role}`} className="space-y-2 mb-4">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      {group.role === 'ai' && (
                                        <>
                                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold bg-violet-500 shadow-sm">
                                            AI
                                          </div>
                                          <span className="text-xs font-semibold text-gray-600">Interviewer</span>
                                        </>
                                      )}
                                    </div>
                                    {group.role !== 'ai' && (
                                      <div className="flex items-center gap-2 ml-auto">
                                        <span className="text-xs font-semibold text-gray-600">
                                          {session.candidateName || 'Candidate'}
                                        </span>
                                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold bg-blue-500 shadow-sm">
                                          C
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="space-y-1.5">
                                    {group.turns.map((turn, turnIdx) => (
                                      <div
                                        key={`turn-${groupIdx}-${turnIdx}`}
                                        className={`flex ${group.role === 'ai' ? 'justify-start' : 'justify-end'}`}
                                      >
                                        <div
                                          className={`px-4 py-3 rounded-xl text-sm max-w-[85%] ${group.role === 'ai'
                                              ? 'bg-gray-100 border border-gray-200 text-gray-800'
                                              : 'bg-blue-50 border border-blue-100 text-blue-900'
                                            }`}
                                        >
                                          {turn.timestamp && (
                                            <div className="text-[10px] text-gray-400 mb-1">
                                              {new Date(turn.timestamp).toLocaleTimeString()}
                                              {turn.duration > 0 ? ` · ${turn.duration}s` : ''}
                                            </div>
                                          )}
                                          <p className="leading-relaxed">
                                            {pickTurnText(turn)}
                                          </p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ));
                            })()
                          ) : (
                            <div className="text-center py-12 text-sm text-gray-400">No transcript available</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

              </div>

              {/* BOTTOM SECTION: 3-Card Score Summary (matches recruiter view) */}
              {(() => {
                const val = sessionForAnalysis.scores?.overall ?? sessionForAnalysis.score;
                const scoreVal = val !== undefined ? val : 0;
                const scoreColor2 = val >= 80 ? '#059669' : val >= 60 ? '#d97706' : '#dc2626';
                const scoreBg = val >= 80 ? 'from-emerald-50 to-green-50' : val >= 60 ? 'from-amber-50 to-yellow-50' : 'from-red-50 to-rose-50';
                const pct = Math.min(val || 0, 100);

                const perSection = sessionForAnalysis.scores?.perSection;
                const sectionData = perSection
                  ? (perSection instanceof Map ? Object.fromEntries(perSection) : (typeof perSection === 'object' ? perSection : {}))
                  : {};
                let sectionEntries = Object.entries(sectionData).filter(([s, v]) => typeof v === 'number' && s !== 'intro' && s !== 'wrap_up');

                // Fallback for rubric/competency-based interviews if sectionEntries only contains generic 'core' or is empty
                if (
                  sectionEntries.length === 0 ||
                  (sectionEntries.length === 1 && sectionEntries[0][0] === 'core')
                ) {
                  let perQ = sessionForAnalysis.scores?.perQuestion;
                  if (!Array.isArray(perQ) || perQ.length === 0) {
                    perQ = sessionForAnalysis.rubric?.perQuestion || [];
                  }
                  const questionRubricMap = new Map();

                  if (Array.isArray(sessionForAnalysis.rubricSelections) && sessionForAnalysis.rubricSelections.length > 0) {
                    let gIdx = 0;
                    for (const sel of sessionForAnalysis.rubricSelections) {
                      const rName = sel.rubricName || 'Core';
                      const indices = Array.isArray(sel.selectedQuestionIndices) ? sel.selectedQuestionIndices : [];
                      for (let k = 0; k < indices.length; k++) {
                        questionRubricMap.set(gIdx, rName);
                        gIdx++;
                      }
                    }
                  } else if (Array.isArray(interviewDetails?.rubrics) && interviewDetails.rubrics.length > 0) {
                    // Try prompt matching against interviewDetails.rubrics for older sessions
                    perQ.forEach((pq, qIdx) => {
                      const pText = (pq.prompt || '').toString().trim().toLowerCase();
                      if (!pText) return;
                      for (const r of interviewDetails.rubrics) {
                        const bank = Array.isArray(r.questionBank) ? r.questionBank : [];
                        const match = bank.some((q) => {
                          const qText = (q.prompt || q.text || q.question || '').toString().trim().toLowerCase();
                          return qText && (qText === pText || pText.includes(qText) || qText.includes(pText));
                        });
                        if (match) {
                          questionRubricMap.set(qIdx, r.name || 'Core');
                          break;
                        }
                      }
                    });

                    // Positional index fallback if prompt matching didn't match all questions
                    if (questionRubricMap.size < perQ.length) {
                      let qIndexCursor = 0;
                      for (const r of interviewDetails.rubrics) {
                        const pickCount = Math.max(1, r.questionsPerInterview || 1);
                        const rName = r.name || 'Core';
                        for (let c = 0; c < pickCount; c++) {
                          if (!questionRubricMap.has(qIndexCursor)) {
                            questionRubricMap.set(qIndexCursor, rName);
                          }
                          qIndexCursor++;
                        }
                      }
                    }
                  }

                  if (questionRubricMap.size > 0 && perQ.length > 0) {
                    const rubricScores = {};
                    const rubricCounts = {};
                    perQ.forEach((pq, qIdx) => {
                      const idx = typeof pq.index === 'number' ? pq.index : (typeof pq.questionIndex === 'number' ? pq.questionIndex : qIdx);
                      const sc = typeof pq.score === 'number' ? pq.score : pq.llmScore;
                      const rName = questionRubricMap.get(idx) || questionRubricMap.get(qIdx);
                      if (rName && typeof sc === 'number') {
                        rubricScores[rName] = (rubricScores[rName] || 0) + sc;
                        rubricCounts[rName] = (rubricCounts[rName] || 0) + 1;
                      }
                    });

                    const computedRubricEntries = Object.entries(rubricScores).map(([rName, totalScore]) => [
                      rName,
                      Math.round(totalScore / rubricCounts[rName]),
                    ]);

                    if (computedRubricEntries.length > 0) {
                      sectionEntries = computedRubricEntries;
                    }
                  }
                }

                const analytics = sessionForAnalysis.analytics || {};
                const strengths = analytics.strengths || [];
                const improvements = analytics.areasForImprovement || [];
                const redFlags = analytics.redFlags || [];
                const standout = analytics.standoutMoments || [];

                const chartColors = ['#6366f1', '#06b6d4', '#f59e0b', '#10b981', '#ec4899', '#8b5cf6'];

                const commScore = sessionForAnalysis.scores?.communication;
                const hasCommSection = sectionEntries.some(([k]) => k.toLowerCase().includes('communication'));
                const radarData = [
                  ...sectionEntries.map(([key, value]) => ({
                    subject: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                    score: value,
                    fullMark: 100,
                  })),
                  ...(commScore != null && !hasCommSection ? [{ subject: 'Communication', score: commScore, fullMark: 100 }] : []),
                ];

                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 mt-2 border-t border-gray-100">

                    {/* Card 1: Overall Score */}
                    <div className={`rounded-2xl p-5 bg-gradient-to-br ${scoreBg} border border-gray-100 shadow-sm flex flex-col items-center justify-center text-center`}>
                      <div className="relative mb-3" style={{ width: 120, height: 120 }}>
                        <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                          <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e5e7eb" strokeWidth="2.5" />
                          <circle cx="18" cy="18" r="15.5" fill="none" stroke={scoreColor2} strokeWidth="2.5"
                            strokeDasharray={`${pct * 0.975} 100`}
                            strokeLinecap="round"
                            className="transition-all duration-700"
                          />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-3xl font-extrabold" style={{ color: scoreColor2 }}>{val ?? '—'}</span>
                          <span className="text-[10px] text-gray-400 font-medium">/ 100</span>
                        </div>
                      </div>
                      <p className="text-sm font-bold text-gray-800 mb-1">AI Interview Score</p>
                      <p className="text-[11px] text-gray-500 mb-3">
                        {val >= 80 ? 'Excellent performance' : val >= 60 ? 'Good, room to improve' : 'Needs improvement'}
                      </p>
                    </div>

                    {/* Card 2: Score Breakdown — Radar Chart */}
                    <div className="rounded-2xl p-5 bg-white border border-gray-100 shadow-sm flex flex-col items-center">
                      <p className="text-sm font-bold text-gray-800 mb-1">AI Score Breakdown</p>
                      {radarData.length === 0 ? (
                        <p className="text-xs text-gray-400 italic mt-8">No section data</p>
                      ) : (
                        <div className="w-full flex flex-col items-center">
                          <ResponsiveContainer width="100%" height={220}>
                            <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
                              <PolarGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                              <PolarAngleAxis
                                dataKey="subject"
                                tick={{ fontSize: 9, fontWeight: 600, fill: '#64748b' }}
                              />
                              <PolarRadiusAxis
                                angle={90}
                                domain={[0, 100]}
                                tick={false}
                                axisLine={false}
                              />
                              <Radar
                                name="Score"
                                dataKey="score"
                                stroke="#6366f1"
                                strokeWidth={2}
                                fill="url(#radarGradientHM)"
                                fillOpacity={0.35}
                                dot={{ r: 4, fill: '#6366f1', stroke: '#fff', strokeWidth: 2 }}
                                animationBegin={200}
                                animationDuration={1200}
                                animationEasing="ease-out"
                              />
                              <defs>
                                <linearGradient id="radarGradientHM" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#818cf8" stopOpacity={0.6} />
                                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0.15} />
                                </linearGradient>
                              </defs>
                              <Tooltip
                                contentStyle={{ borderRadius: 12, fontSize: 11, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                                formatter={(v) => [`${v}%`, 'Score']}
                              />
                            </RadarChart>
                          </ResponsiveContainer>
                          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-1">
                            {radarData.map((d, i) => (
                              <div key={d.subject} className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: chartColors[i % chartColors.length] }} />
                                <span className="text-[10px] text-gray-500 font-medium">{d.subject}</span>
                                <span className="text-[10px] font-bold text-gray-800">{d.score}%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Card 3: AI Insights */}
                    <div className="rounded-2xl p-5 bg-white border border-gray-100 shadow-sm flex flex-col">
                      <p className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-1.5">
                        <span className="bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">✦</span> AI Insights
                      </p>
                      <div className="flex-1 overflow-y-auto max-h-64 pr-1" style={{ scrollbarWidth: 'thin' }}>
                        {[
                          ...strengths.slice(0, 3).map(s => ({ text: s, type: 'strength' })),
                          ...improvements.slice(0, 2).map(s => ({ text: s, type: 'improve' })),
                          ...redFlags.slice(0, 2).map(s => ({ text: s, type: 'flag' })),
                          ...standout.slice(0, 2).map(s => ({ text: s, type: 'standout' })),
                        ].map((item, idx) => {
                          const config = {
                            strength: { label: 'Strength', bg: 'bg-emerald-500' },
                            improve: { label: 'Improve', bg: 'bg-amber-500' },
                            flag: { label: 'Flag', bg: 'bg-red-500' },
                            standout: { label: 'Standout', bg: 'bg-indigo-500' },
                          }[item.type];
                          return (
                            <div key={idx} className={`flex items-center gap-3 py-2.5 ${idx > 0 ? 'border-t border-gray-50' : ''}`}>
                              <div className="flex-1 min-w-0">
                                <p className="text-[11.5px] text-gray-700 leading-relaxed">{item.text}</p>
                              </div>
                              <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[9px] font-semibold text-white ${config.bg}`}>
                                {config.label}
                              </span>
                            </div>
                          );
                        })}
                        {strengths.length === 0 && improvements.length === 0 && redFlags.length === 0 && standout.length === 0 && (
                          <p className="text-xs text-gray-400 italic text-center py-4">No insights available</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

            </div>
          )}
        </div>

{/* ════════ PROCTORING TAB ════════ */}
          {activeTab === 'proctoring' && (
            <div className="px-6 py-4 overflow-y-auto">
              {interviewProctoringLoading ? (
                <div className="py-16 text-center text-gray-400 text-sm">Loading proctoring details...</div>
              ) : interviewProctoringData?.error ? (
                <div className="py-16 text-center text-red-500 text-sm">{interviewProctoringData.message}</div>
              ) : (
                <InterviewProctoringContent data={interviewProctoringData || null} />
              )}
            </div>
          )}

        {/* ── Footer ── */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-3 rounded-b-2xl flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}

export default InterviewSessionModal;
